/**
 * clinicalFieldValidators.js  — TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 1
 *
 * Single source of truth for input normalization and validation rules
 * used by clinical patient-data forms (clinical profile editor, patient
 * onboarding, patient settings, admin patient editor). Designed to be
 * imported in any screen so onboarding / edit / settings / admin all
 * apply identical rules.
 *
 * Scope on purpose minimal — just the rules called for by UAT Phase 1:
 *   - 4-digit year inputs (1900 .. current year)
 *   - Height in centimetres (integer, 50..272)
 *   - Weight in kilograms (one decimal place, 2.0..400.0)
 *   - Generic free-text whitespace normalization
 *   - Date sanity (not-in-future, end ≥ start)
 *
 * No backend logic, no API contracts, no localization concerns.
 */

export const CURRENT_YEAR = new Date().getUTCFullYear();
export const MIN_YEAR = 1900;

// Height / weight limits — wide enough to cover medical edge cases
// (paediatric 50 cm through tallest human ~272 cm; ~2 kg neonate
// through bariatric ~400 kg) but tight enough to reject typos.
export const HEIGHT_CM_MIN = 50;
export const HEIGHT_CM_MAX = 272;
export const WEIGHT_KG_MIN = 2;
export const WEIGHT_KG_MAX = 400;

// ── Year ───────────────────────────────────────────────────────────────────
/**
 * Normalise a year input: keep digits only, cap at 4 chars.
 * Returns a string (empty allowed → caller decides if required).
 */
export function normalizeYearInput(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D+/g, '').slice(0, 4);
}

/**
 * Validate a year string.
 * @returns {{ valid: boolean, error?: string }}
 *   Empty value is considered valid (use `required` separately).
 */
export function validateYear(value, { min = MIN_YEAR, max = CURRENT_YEAR } = {}) {
  if (value === '' || value === null || value === undefined) {
    return { valid: true };
  }
  const s = String(value);
  if (!/^\d{4}$/.test(s)) {
    return { valid: false, error: 'Enter a 4-digit year' };
  }
  const n = Number(s);
  if (Number.isNaN(n)) return { valid: false, error: 'Enter a 4-digit year' };
  if (n < min) return { valid: false, error: `Year must be ${min} or later` };
  if (n > max) return { valid: false, error: `Year cannot be after ${max}` };
  return { valid: true };
}

// ── Height (cm, integer) ───────────────────────────────────────────────────
export function normalizeHeightCmInput(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D+/g, '').slice(0, 3);
}

export function validateHeightCm(value) {
  if (value === '' || value === null || value === undefined) {
    return { valid: true };
  }
  const s = String(value);
  if (!/^\d+$/.test(s)) {
    return { valid: false, error: 'Height must be a whole number of centimetres' };
  }
  const n = Number(s);
  if (n < HEIGHT_CM_MIN) return { valid: false, error: `Height must be at least ${HEIGHT_CM_MIN} cm` };
  if (n > HEIGHT_CM_MAX) return { valid: false, error: `Height cannot exceed ${HEIGHT_CM_MAX} cm` };
  return { valid: true };
}

// ── Weight (kg, one decimal) ───────────────────────────────────────────────
/**
 * Allow digits + at most one '.' + at most one digit after it. Strip
 * everything else. Caller still needs to call `validateWeightKg` for
 * range checks.
 */
export function normalizeWeightKgInput(value) {
  if (value === null || value === undefined) return '';
  let s = String(value).replace(/[^\d.]/g, '');
  // collapse multiple dots → keep first
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    // keep at most one decimal digit
    const [whole, frac = ''] = s.split('.');
    s = frac ? `${whole}.${frac.slice(0, 1)}` : `${whole}.`;
    // if user just typed a dot at end, leave it (UX) but disallow trailing-only dot at validate time
  }
  // cap whole-part length at 3 (so values like 9999.0 are rejected up-front)
  const [whole, frac] = s.split('.');
  if (whole && whole.length > 3) {
    s = whole.slice(0, 3) + (s.includes('.') ? '.' + (frac || '') : '');
  }
  return s;
}

export function validateWeightKg(value) {
  if (value === '' || value === null || value === undefined) {
    return { valid: true };
  }
  const s = String(value);
  if (!/^\d{1,3}(\.\d)?$/.test(s)) {
    return { valid: false, error: 'Weight must be a number with at most one decimal' };
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return { valid: false, error: 'Enter a valid weight' };
  if (n < WEIGHT_KG_MIN) return { valid: false, error: `Weight must be at least ${WEIGHT_KG_MIN} kg` };
  if (n > WEIGHT_KG_MAX) return { valid: false, error: `Weight cannot exceed ${WEIGHT_KG_MAX} kg` };
  return { valid: true };
}

// ── Text whitespace normalization ──────────────────────────────────────────
/**
 * Trim leading/trailing whitespace and collapse runs of inner whitespace
 * to single spaces. Empty input → empty string. Safe to call on every
 * change handler since it only affects whitespace.
 */
export function normalizeWhitespace(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
}

/**
 * Lighter version that keeps inner whitespace intact (so the user can
 * actually type "John  Smith" while editing) — only used at save time.
 */
export function normalizeWhitespaceOnSave(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
}

// ── Date sanity ────────────────────────────────────────────────────────────
function asDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function validateNotInFuture(value, { fieldLabel = 'Date' } = {}) {
  if (!value) return { valid: true };
  const d = asDate(value);
  if (!d) return { valid: false, error: `${fieldLabel} is not a valid date` };
  if (d.getTime() > Date.now()) {
    return { valid: false, error: `${fieldLabel} cannot be in the future` };
  }
  return { valid: true };
}

export function validateDateOrder(startValue, endValue, {
  startLabel = 'Start date', endLabel = 'End date',
} = {}) {
  if (!startValue || !endValue) return { valid: true };
  const s = asDate(startValue);
  const e = asDate(endValue);
  if (!s || !e) return { valid: false, error: `${startLabel} / ${endLabel} not a valid date` };
  if (e.getTime() < s.getTime()) {
    return { valid: false, error: `${endLabel} cannot be before ${startLabel}` };
  }
  return { valid: true };
}

// ── Convenience: detect "year" column heuristically ────────────────────────
// Used by generic editors that didn't tag the column with a type
// originally. Matches column ids like "year", "since" and labels
// containing "(year)".
export function isYearColumn(col) {
  if (!col) return false;
  const id = String(col.id || '').toLowerCase();
  if (id === 'year' || id === 'since' || id.endsWith('_year')) return true;
  const label = String(col.label || '').toLowerCase();
  return label.includes('(year)') || label.endsWith(' year');
}
